/* eslint-disable no-console */
// Inspired by: https://dev.to/aduranil/how-to-use-websockets-with-redux-a-step-by-step-guide-to-writing-understanding-connecting-socket-middleware-to-your-project-km3
import {
    type Action,
    type MiddlewareAPI,
    type ThunkAction,
    type ThunkDispatch,
    type ThunkMiddleware,
    type UnknownAction,
} from '@reduxjs/toolkit';

import { getSocketURL, type SocketType, SocketTypes } from 'services/socketHelpers';
import { type authSlice, type AuthSliceState } from 'store/auth/authSlice';
import {
    type NewNotification,
    receiveNewNotifications,
} from 'store/notification/notificationThunks';

export interface SocketAction extends Action {
    socketType: SocketType;
    messageType: string;
    message: string;
}

interface SocketMessage {
    type: string;
    message: string;
    payload: unknown;
}

interface PartialState {
    [authSlice.name]: AuthSliceState;
}

export type SocketMiddleware = MiddlewareAPI<
    ThunkDispatch<PartialState, undefined, UnknownAction>,
    PartialState
>;

interface SocketTemplate {
    store: SocketMiddleware;
    socketType: SocketType;
    socketUrl: string;
    messageMap: Record<string, (store: SocketMiddleware, message: SocketMessage) => void>;
}
type SocketMap = Record<
    SocketType,
    {
        template: Omit<SocketTemplate, 'store'> & {
            store: SocketMiddleware | null;
        };
        actions: {
            WS_CONNECT: (
                store: SocketMiddleware,
                action: SocketAction,
                template: SocketTemplate,
            ) => SocketTemplate;
            WS_SEND?: (store: SocketMiddleware, action: SocketAction) => object;
        };
    }
>;

const wsConnected = (socketType: SocketType, host: Event) => ({
    type: 'WS_CONNECTED',
    socketType,
    host,
});
const wsDisconnected = (socketType: SocketType) => ({ type: 'WS_DISCONNECTED', socketType });
const wsReconnect = (socketType: SocketType) => ({ type: 'WS_RECONNECT', socketType });

interface Socket {
    isOpen: () => void;
    close: () => void;
    send: (message: string) => void;
}
const sockets: Record<SocketType, Socket | null> = { [SocketTypes.NOTIFICATION]: null };

const socketMap: SocketMap = {
    [SocketTypes.NOTIFICATION]: {
        template: {
            store: null,
            socketType: SocketTypes.NOTIFICATION,
            socketUrl: getSocketURL(SocketTypes.NOTIFICATION),
            messageMap: {
                STATUS: (_store, message) =>
                    console.log(`Notification WebSocket Status: ${message.message}`),
                NOTIFICATION: (store, message) =>
                    store.dispatch(
                        receiveNewNotifications(
                            message.payload as NewNotification[],
                        ) as ThunkAction<void, PartialState, unknown, UnknownAction>,
                    ),
            },
        },
        actions: {
            WS_CONNECT: (store, _action, template) => ({
                ...template,
                store,
            }),
        },
    },
};

const openSocket = ({
    store,
    socketType,
    socketUrl,
    messageMap,
    onOpenAction,
    onCloseAction,
    onReconnectAttempt,
    checkIfDisconnected,
}: SocketTemplate & {
    onOpenAction?: () => void;
    onCloseAction?: () => void;
    onReconnectAttempt?: () => void;
    checkIfDisconnected?: () => boolean;
}): Socket => {
    let socket: WebSocket | null = null;
    let timerId: NodeJS.Timeout;
    let pingSendsCounter = 0; // "unacknowledged" ping sends

    const cancelKeepAlive = () => {
        pingSendsCounter = 0;
        if (timerId) {
            clearTimeout(timerId);
        }
    };

    const keepAlive = () => {
        const timeout = 20000;
        if (socket?.readyState === socket?.OPEN) {
            socket?.send(JSON.stringify({ type: 'KEEP-ALIVE' }));
        }
        pingSendsCounter += 1;
        if (pingSendsCounter >= 3) {
            console.warn(
                `No Server Response in ${
                    (timeout * pingSendsCounter) / 1000
                } seconds - Reconnecting ${socketType}`,
            );
            onReconnectAttempt?.();
            store.dispatch(wsReconnect(socketType));
        } else if (checkIfDisconnected?.() && pingSendsCounter >= 1) {
            console.warn(`Server unexpectedly disconnected - Reconnecting ${socketType}`);
            onReconnectAttempt?.();
            store.dispatch(wsReconnect(socketType));
        } else {
            timerId = setTimeout(() => keepAlive(), timeout);
        }
    };

    const isOpen = () => socket != null;

    const close = () => {
        socket?.close();
    };

    const send = (message: string) => {
        if (socket?.readyState === socket?.OPEN) {
            socket?.send(message);
        } else {
            setTimeout(() => socket?.send(message), 2000);
        }
    };

    // make sure any old sockets aren't doing any work
    cancelKeepAlive();

    // connect to the remote host
    socket = new WebSocket(`${socketUrl}/ws`);

    // websocket handlers
    socket.onopen = (event) => {
        // authenticate
        const token = store.getState().auth.accessToken;
        const authBody = JSON.stringify({ type: 'AUTH', token });
        socket.send(authBody);

        console.log('websocket open', event);
        store.dispatch(wsConnected(socketType, event));

        keepAlive();

        onOpenAction?.();
    };

    socket.onclose = () => {
        console.log(`${socketType} was closed.`);
        onCloseAction?.();
        store.dispatch(wsDisconnected(socketType));
    };

    socket.onmessage = (event: { data: string }) => {
        // reset ping sends counter
        pingSendsCounter = 0;

        const message = JSON.parse(event.data) as SocketMessage;
        if (message && messageMap[message.type]) {
            messageMap[message.type](store, message);
        } else {
            console.log('Unknown message type', message);
        }
    };

    return {
        isOpen,
        close,
        send,
    };
};

const socketMiddleware = (): ThunkMiddleware<PartialState, UnknownAction> => {
    const isSocketOpen = (socketType: SocketType) => sockets[socketType]?.isOpen();

    const openMappedSocket = (store: SocketMiddleware, action: SocketAction) => {
        sockets[action.socketType] = openSocket(
            socketMap[action.socketType].actions.WS_CONNECT(
                store,
                action,
                socketMap[action.socketType].template as SocketTemplate,
            ),
        );
    };

    const sendOnMappedSocket = (store: SocketMiddleware, action: SocketAction) => {
        console.log(
            `sending a message ${action.messageType} to socket ${action.socketType}`,
            action.message,
        );
        const socketWSSend = socketMap[action.socketType].actions.WS_SEND;
        sockets[action.socketType]?.send(
            JSON.stringify(
                socketWSSend
                    ? socketWSSend(store, action)
                    : { type: action.messageType, message: action.message },
            ),
        );
    };

    const actionMap: Record<string, (store: SocketMiddleware, action: SocketAction) => void> = {
        WS_CONNECT: (store, action) => {
            if (isSocketOpen(action.socketType)) {
                sockets[action.socketType]!.close();
            }

            openMappedSocket(store, action);
        },
        WS_ENSURE_CONNECTED: (store, action) => {
            if (!isSocketOpen(action.socketType)) {
                openMappedSocket(store, action);
            }
        },
        WS_RECONNECT: (store, action) => {
            openMappedSocket(store, action);
        },
        WS_DISCONNECT: (_store, action) => {
            if (isSocketOpen(action.socketType)) {
                sockets[action.socketType]!.close();
            }
        },
        WS_SEND: (store, action) => {
            if (!isSocketOpen(action.socketType)) {
                setTimeout(() => sendOnMappedSocket(store, action), 2000);
            } else {
                sendOnMappedSocket(store, action);
            }
        },
    };

    // the middleware part of this function
    return (store) => (next) => (action) => {
        const typedAction = action as SocketAction;
        if (typedAction && actionMap[typedAction.type]) {
            return actionMap[typedAction.type](store, typedAction);
        }
        return next(action);
    };
};

export default socketMiddleware();
